import { API_BASE_URL, ApiResponse, RequestMethod } from "./config";

// Type for request options
interface RequestOptions {
  method: RequestMethod;
  headers?: HeadersInit;
  body?: BodyInit | null;
  withAuth?: boolean;
  customConfig?: RequestInit;
}

// Type for query parameters
interface QueryParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * ApiClient class for handling API requests
 */
class ApiClient {
  /**
   * Get the authentication token from local storage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem("authToken");
  }

  /**
   * Convert query parameters to a URL-encoded string
   */
  private formatQueryParams(params: QueryParams): string {
    const validParams = Object.entries(params).filter(
      ([_, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (typeof value === "number" ||
          (typeof value === "string" && value.trim() !== ""))
    );

    if (!validParams.length) return "";

    const queryString = validParams
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

    return `?${queryString}`;
  }

  /**
   * Add authentication headers to the request if needed
   */
  private getHeaders(withAuth: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (withAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Handle API response and errors
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");

    if (!response.ok) {
      if (response.status === 401) {
        // Clear auth token if unauthorized
        localStorage.removeItem("authToken");
        // You might want to redirect to login or refresh token here
      }

      // Try to parse error as JSON
      if (isJson) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || `ERROR_${response.status}`,
            message: errorData.message || errorData.error?.message || "An error occurred",
            details: errorData.details || errorData,
          },
        };
      }

      // Non-JSON error
      return {
        success: false,
        error: {
          code: `ERROR_${response.status}`,
          message: response.statusText || "An unknown error occurred",
        },
      };
    }

    // Success with no content
    if (response.status === 204) {
      return { success: true };
    }

    // Success with JSON content
    if (isJson) {
      const data = await response.json();
      return { success: true, data };
    }

    // Success with non-JSON content
    return { success: true, data: undefined };
  }

  /**
   * Make an API request
   */
  public async request<T>(
    endpoint: string,
    options: RequestOptions
  ): Promise<ApiResponse<T>> {
    const { method, body, withAuth = false, customConfig = {} } = options;

    try {
      const headers = this.getHeaders(withAuth);

      const config: RequestInit = {
        method,
        headers,
        ...customConfig,
      };

      if (body) {
        config.body = typeof body === "string" ? body : JSON.stringify(body);
      }

      const fullUrl = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(fullUrl, config);

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error("API request failed:", error);
      return {
        success: false,
        error: {
          code: "REQUEST_FAILED",
          message:
            error instanceof Error ? error.message : "Network request failed",
        },
      };
    }
  }

  /**
   * Make a GET request
   */
  public async get<T>(
    endpoint: string,
    queryParams?: QueryParams,
    withAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    const queryString = queryParams ? this.formatQueryParams(queryParams) : "";

    return this.request<T>(`${endpoint}${queryString}`, {
      method: RequestMethod.GET,
      withAuth,
    });
  }

  /**
   * Make a POST request
   */
  public async post<T>(
    endpoint: string,
    data?: any,
    withAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: RequestMethod.POST,
      body: data,
      withAuth,
    });
  }

  /**
   * Make a PUT request
   */
  public async put<T>(
    endpoint: string,
    data?: any,
    withAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: RequestMethod.PUT,
      body: data,
      withAuth,
    });
  }

  /**
   * Make a PATCH request
   */
  public async patch<T>(
    endpoint: string,
    data?: any,
    withAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: RequestMethod.PATCH,
      body: data,
      withAuth,
    });
  }

  /**
   * Make a DELETE request
   */
  public async delete<T>(
    endpoint: string,
    withAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: RequestMethod.DELETE,
      withAuth,
    });
  }

  /**
   * Upload a file
   */
  public async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
    withAuth: boolean = true
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const headers: HeadersInit = {};
    if (withAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return this.request<T>(endpoint, {
      method: RequestMethod.POST,
      body: formData,
      withAuth,
      customConfig: {
        headers, // Don't include Content-Type, let browser set it with boundary
      },
    });
  }
}

// Create a singleton instance
export const apiClient = new ApiClient();

export default apiClient;
